// React
import React, { useEffect, useState, useCallback } from 'react';

// Prop types
import PropTypes from 'prop-types';
import * as appPropTypes from '../../appPropTypes';

// Component
import { FormattedMessage, useIntl } from 'react-intl';
import VideoView from '../../VideoContainers/VideoView';

// Redux
import { connect } from 'react-redux';
import { withRoomContext } from '../../../RoomContext';
import * as settingsActions from '../../../actions/settingsActions';
import * as peerVolumeActions from '../../../actions/peerVolumeActions';

// Mui styles
import { withStyles, MuiThemeProvider, createTheme } from '@material-ui/core/styles';

// Mui core
import Button from '@material-ui/core/Button';
import Slider from '@material-ui/core/Slider';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';

// Mui icon
import MicIcon from '@material-ui/icons/Mic';
import VideocamIcon from '@material-ui/icons/Videocam';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import FlipCameraAndroidIcon from '@material-ui/icons/FlipCameraAndroid';
import classnames from 'classnames';

// mic hark
import hark from 'hark';

const styles = (theme) => ({
	root : {
		width           : '100%',
		backgroundColor : theme.palette.background.paper,
		overflowY       : 'auto'
	},
	circle : {
		width           : '3.5rem',
		height          : '3.5rem',
		borderRadius    : '3.5rem',
		backgroundColor : '#999898',
		display         : 'flex',
		justifyContent  : 'center',
		alignItems      : 'center',
		margin          : '0.5rem 0'
	},
	hostName : {
		fontSize : '2rem',
		color    : 'white'
	},
	buttonGroup : {
		display        : 'flex',
		justifyContent : 'center'
	},
	button : {
		width           : '4.5rem',
		height          : '4.5rem',
		borderRadius    : '4.5rem',
		fontSize        : '0.6rem',
		backgroundColor : 'white',
		color           : '#999898',
		boxShadow       : '2px 2px 4px #aaa',
		justifyContent  : 'center',
		alignItems      : 'center',
		display         : 'grid',
		lineHeight      : 1.5,
		whiteSpace      : 'break-spaces',
		margin          : '0 0.5rem 1rem 0.5rem',
		'&:hover'       : {
			backgroundColor : '#f50057'
		}
	},
	buttonIcon : {
		width  : '2rem',
		height : '2rem'
	},
	formControl : {
		width         : '100%',
		display       : 'flex',
		flexDirection : 'row',
		color         : '#999898'
	},
	previewVideo : {
		width       : '90%',
		margin      : '0 auto 5% auto',
		aspectRatio : 1
	},
	aspectRatio : {
		aspectRatio : 1
	},
	deviceTitle : {
		width      : '30%',
		color      : '#999898',
		display    : 'flex',
		alignItems : 'center'
	},
	deviceName : {
		fontSize   : '0.6rem',
		marginLeft : '0.3rem'
	},
	device : {
		width          : '70%',
		display        : 'flex',
		flexDirection  : 'column',
		justifyContent : 'center'
	},
	setting : {
		padding : '2% 5%',
		display : 'flex'
	},
	selectEmpty : {
		fontSize     : '0.8rem',
		color        : '#999898',
		padding      : '0.4rem',
		background   : 'white',
		borderRadius : '0.4rem',
		boxShadow    : '2px 2px 4px #aaa'
	},
	playSoundBtn : {
		display        : 'flex',
		justifyContent : 'end',
		cursor         : 'pointer',
		color          : '#999898',
		fontSize       : '0.6rem'
	},
	soundTest : {
		padding       : '0 5%',
		display       : 'flex',
		flexDirection : 'row-reverse'
	},
	noVideo : {
		width          : '93%',
		margin         : '0 auto 5% auto',
		aspectRatio    : 1,
		background     : '#999898',
		borderRadius   : '2rem',
		display        : 'flex',
		justifyContent : 'center',
		alignItems     : 'center',
		color          : 'white'
	}
});

const theme = createTheme({
	overrides : {
		MuiSlider : {
			root : {
				height          : '1rem',
				borderRadius    : '0.3rem',
				padding         : '0!important',
				backgroundColor : 'white'
			},
			thumb : {
				height                       : '0',
				width                        : '0',
				'&:focus, &:hover, &$active' : {
					boxShadow : 'inherit'
				}
			},
			track : {
				height          : '1rem',
				borderRadius    : '0.3rem',
				backgroundColor : '#9ec317'
			},
			rail : {
				height          : '1rem',
				borderRadius    : '0.3rem',
				backgroundColor : 'white'
			}
		},
		MuiInput : {
			underline : {
				'&:before' : {
					borderBottom : 'none'
				},
				'&:after' : {
					borderBottom : 'none'
				},
				'&:not(.Mui-disabled):hover::before' : {
					borderBottom : 'none'
				},
				'&.Mui-disabled:before' : {
					borderBottomStyle : 'none'
				}
			}
		},
		MuiSelect : {
			select : {
				'&:focus' : {
					borderRadius : '0.4rem'
				}
			}
		}
	}
});

let lastVolume = -100;

const modifyWebcamLabel = (devicesArray) =>
{
	// move devices info to this array. Because MediaDeviceInfo cannot be written.
	const deviceObjectsAr = [];

	let itemIndex = 1;

	devicesArray.forEach((device) =>
	{
		// avoid duplication
		const deviceIdSearch = deviceObjectsAr.find((obj) => obj.deviceId===device.deviceId);

		if (device.deviceId && !deviceIdSearch)
		{
			const deviceObj = {};

			if (!device.label) // no label;
			{
				deviceObj.label = `カメラ${itemIndex}`;
			}
			else if (/facing front/i.test(device.label))
			{
				deviceObj.label = '前面カメラ';
			}
			else if (/facing back/i.test(device.label))
			{
				deviceObj.label = '背面カメラ';
			}
			else
			{
				deviceObj.label = device.label.replace(/(Camera|camera)/, 'カメラ');
			}

			deviceObj.deviceId = device.deviceId;

			deviceObjectsAr.push(deviceObj);
			itemIndex += 1;
		}
	});

	return deviceObjectsAr;
};

const modifyAudioDevicesLabel = (devicesArray) =>
{

	// move devices info to this array. Because MediaDeviceInfo cannot be written.
	const deviceObjectsAr = [];

	let itemIndex = 1;

	devicesArray.forEach((device) =>
	{
		// avoid duplication
		const deviceIdSearch = deviceObjectsAr
			.find((obj) => obj.deviceId === device.deviceId);

		if (device.deviceId && !deviceIdSearch)
		{
			const deviceObj = {};

			if (device.label)
			{
				deviceObj.label = device.label
					.replace(/(Speakerphone|speakerphone)/, 'スピーカーフォン')
					.replace(/(Headset|headset)/, 'ヘッドセット')
					.replace(/(Earpiece|earpiece|Earphone|earphone)/, 'イヤフォン')
					.replace(/Microphone|microphone/, 'マイク');
			}
			else
			{
				deviceObj.label = `マイク${itemIndex}`;
			}

			deviceObj.deviceId = device.deviceId;

			deviceObjectsAr.push(deviceObj);
			itemIndex += 1;
		}
	});

	return deviceObjectsAr;
};

const DeviceSettings = ({
	roomClient,
	me,
	room,
	settings,
	classes,
	videoMuted,
	setSelectedWebcamDevice,
	setSelectedAudioDevice,
	setSelectedAudioOutputDevice,
	isHost,
	volumeCheck,
	setChangePersonDialogOpen,
	peerVolumes,
	endOfcontentRef,
	setPeerVolume
}) =>
{
	const intl = useIntl();

	const [ videoTrack, setVideoTrack ] = useState(false);
	const [ micVolume, setMicVolume ] = useState(0);
	const [ videoVisible, setVideoVisible ] = useState(false);

	const playSound = () =>
	{
		roomClient.playSound();
	};

	let webcams;

	if (me.webcamDevices)
	{
		const webcamsAr = Object.values(me.webcamDevices);

		webcams = modifyWebcamLabel(webcamsAr);
	}
	else
	{
		webcams = [];
	}

	const handleCameraSelected = useCallback(async (newDeviceId) =>
	{
		if (settings.selectedWebcam !== newDeviceId)
		{
			if (room.state === 'connected')
			{
				await roomClient.updateDevices();

				await roomClient.updateWebcam({
					restart     : true,
					newDeviceId : newDeviceId
				});
			}
			else
			{
				setSelectedWebcamDevice(newDeviceId);
			}
		}
	}, [ room.state, roomClient, setSelectedWebcamDevice, settings.selectedWebcam ]);

	useEffect(() =>
	{
		const caller = async () =>
		{
			const stream = await navigator.mediaDevices.getUserMedia(
				{
					video :
					{
						deviceId : { ideal: settings.selectedWebcam }
					}
				});

			const newTrack = stream.getVideoTracks();

			if (newTrack.length > 0)
			{
				setVideoTrack(newTrack[0]);
				setVideoVisible(true);
			}
			else
			{
				setVideoVisible(false);
			}

		};

		caller();
	}, [ settings.selectedWebcam ]);

	useEffect(() =>
	{
		navigator.mediaDevices.getUserMedia({ audio : {
			deviceId         : { ideal: settings.selectedAudioDevice },
			sampleRate       : 48000,
			channelCount     : 1,
			volume           : 1.0,
			autoGainControl  : true,
			echoCancellation : true,
			noiseSuppression : true
		} }).then((stream) =>
		{
			// if no audio channel
			if (volumeCheck)
			{
				const options = {
					play      : false,
					interval  : 10,
					threshold : settings.noiseThreshold,
					history   : 100
				};

				const speechEvents = hark(stream, options);

				speechEvents.on('volume_change', function(volume)
				{

					// Update only if there is a bigger diff 
					if (Math.abs(volume - lastVolume) > 0.5)
					{
						// Decay calculation: keep in mind that volume range is -100 ... 0 (dB)
						// This makes decay volume fast if difference to last saved value is big
						// and slow for small changes. This prevents flickering volume indicator
						// at low levels
						if (volume < lastVolume)
						{
							volume =
						lastVolume -
						Math.pow(
							(volume - lastVolume) /
							(100 + lastVolume)
							, 2
						) * 10;
						}

						lastVolume = volume;

						setPeerVolume(me.id, volume);
					}
				});
			}
		});

	},	[
		settings.selectedAudioDevice,
		volumeCheck,
		me.id,
		setPeerVolume,
		settings.noiseThreshold
	]);

	let audioDevices;

	if (me.audioDevices)
	{
		const audioDevicesAr = Object.values(me.audioDevices);

		audioDevices = modifyAudioDevicesLabel(audioDevicesAr);
	}
	else
	{
		audioDevices = [];
	}

	const handleMicSelected = useCallback(async (newDeviceId) =>
	{
		if (settings.selectedAudioDevice !== newDeviceId)
		{
			if (room.state === 'connected')
			{
				await roomClient.updateMic({
					restart     : true,
					newDeviceId : newDeviceId
				});
			}
			else
			{
				setSelectedAudioDevice(newDeviceId);
			}
		}
	}, [ room.state, roomClient, settings.selectedAudioDevice, setSelectedAudioDevice ]);

	useEffect(() =>
	{

		if (peerVolumes)
		{
			let volume = 0;

			if (peerVolumes[me.id] > settings.noiseThreshold)
			{
				volume = Math.round(
					(peerVolumes[me.id] - settings.noiseThreshold) *
					-100/(settings.noiseThreshold));
			}
			setMicVolume(volume);
		}
	}, [ me.id, settings.noiseThreshold, peerVolumes, setMicVolume ]);

	let audioOutputDevices;

	if (me.audioOutputDevices)
	{
		audioOutputDevices = Object.values(me.audioOutputDevices);
	}
	else
	{
		audioOutputDevices = [];
	}

	const handleSpeakerSelected = useCallback(async (newDeviceId) =>
	{
		if (settings.selectedAudioOutputDevice !== newDeviceId)
		{
			if (room.state === 'connected')
			{
				await roomClient.changeAudioOutputDevice(newDeviceId);
			}
			else
			{
				setSelectedAudioOutputDevice(newDeviceId);
			}
		}
	}, [
		room.state,
		roomClient,
		setSelectedAudioOutputDevice,
		settings.selectedAudioOutputDevice
	]);

	/*
	useEffect(async () =>
	{
	}, [ settings.selectedAudioOutputDevice ]);
	*/

	return (
		<MuiThemeProvider theme={theme}>
			<React.Fragment>
				{/* {isHost &&
					<Box className={classes.buttonGroup}>
						<Box className={classes.circle}>
							<Typography className={classes.hostName}>U</Typography>
						</Box>
					</Box>
				} */}
				<Box
					className={classnames(
						classes.previewVideo,
						!isHost ? classes.aspectRatio : null
					)}
				>
					{ videoMuted ?
						<Typography className={classes.noVideo} variant='h6' id='tableTitle' component='div'>
							ビデオは使用しません
						</Typography>
						:
						<VideoView
							videoTrack={videoTrack}
							videoVisible={videoVisible}
						/>
					}
				</Box>
				{isHost &&
					<Box className={classes.buttonGroup}>
						{/* <Button className={classes.button}>
							<Box>
								<MicIcon className={classes.buttonIcon} />
								<p>
									<FormattedMessage
										id='label.mic'
										defaultMessage='Mic'
									/>
								</p>
							</Box>
						</Button>
						<Button className={classes.button}>
							<Box>
								<VideocamIcon className={classes.buttonIcon} />
								<p>
									<FormattedMessage
										id='settings.camera'
										defaultMessage='Camera'
									/>
								</p>
							</Box>
						</Button> */}
						<Button
							className={classes.button}
							onClick={() => setChangePersonDialogOpen(true)}
						>
							<Box>
								<FlipCameraAndroidIcon className={classes.buttonIcon} />
								<p>
									<FormattedMessage
										id='settings.changePerson'
										defaultMessage='Change person in charge'
									/>
								</p>
							</Box>
						</Button>
					</Box>
				}
				<Box className={classes.setting}>
					<Box className={classes.deviceTitle}>
						<MicIcon fontSize='large'/>
						<Typography className={classes.deviceName}>
							マイク音量
						</Typography>
					</Box>
					<Box className={classes.device}>
						<Slider
							value={typeof micVolume === 'number' ? micVolume : 0}
							aria-labelledby='input-slider'
							max={40}
						/>
					</Box>
				</Box>
				<form className={classes.setting} autoComplete='off'>
					<FormControl className={classes.formControl}>
						<Box className={classes.deviceTitle}>
							<VideocamIcon fontSize='large'/>
							<Typography className={classes.deviceName}>
								<FormattedMessage
									id='settings.camera'
									defaultMessage='Camera'
								/>
							</Typography>
						</Box>
						<Box className={classes.device}>
							<Select
								defaultValue={settings.selectedWebcam || ''}
								onChange={(event) =>
								{
									if (event.target.value)
									{
										handleCameraSelected(event.target.value);
									}
								}}
								displayEmpty
								name={intl.formatMessage({
									id             : 'settings.camera',
									defaultMessage : 'Camera'
								})}
								autoWidth
								classes={{
									root : classes.selectEmpty
								}}
								disabled={webcams.length === 0 || me.webcamInProgress}
							>
								{ webcams.map((webcam, index) =>
								{
									return (
										<MenuItem key={index} value={webcam.deviceId}>
											{webcam.label}
											{webcam.deviceId === settings.selectedWebcam && '(選択済)'}
										</MenuItem>
									);
								})}
							</Select>
						</Box>
					</FormControl>
				</form>
				<form className={classes.setting} autoComplete='off'>
					<FormControl className={classes.formControl}>
						<Box className={classes.deviceTitle}>
							<MicIcon fontSize='large'/>
							<Typography className={classes.deviceName}>
								<FormattedMessage
									id='label.mic'
									defaultMessage='Mic'
								/>
							</Typography>
						</Box>
						<Box className={classes.device}>
							<Select
								defaultValue={settings.selectedAudioDevice || ''}
								onChange={(event) =>
								{
									if (event.target.value)
									{
										handleMicSelected(event.target.value);
									}
								}}
								displayEmpty
								name={intl.formatMessage({
									id             : 'settings.audio',
									defaultMessage : 'Audio input device'
								})}
								autoWidth
								classes={{
									root : classes.selectEmpty
								}}
								disabled={audioDevices.length === 0 || me.audioInProgress}
							>
								{ audioDevices.map((audio, index) =>
								{
									return (
										<MenuItem key={index} value={audio.deviceId}>
											{audio.label}
											{audio.deviceId === settings.selectedAudioDevice && ' (選択済)'}
										</MenuItem>
									);
								})}
							</Select>
						</Box>
					</FormControl>
				</form>
				{ 'audioOutputSupportedBrowsers' in window.config &&
				window.config.audioOutputSupportedBrowsers.includes(me.browser.name) &&
				<form className={classes.setting} autoComplete='off'>
					<FormControl className={classes.formControl}>
						<Box className={classes.deviceTitle}>
							<VolumeUpIcon fontSize='large'/>
							<Typography className={classes.deviceName}>
								<FormattedMessage
									id='settings.speaker'
									defaultMessage='Speaker'
								/>
							</Typography>
						</Box>
						<Box className={classes.device}>
							<Select
								defaultValue={settings.selectedAudioOutputDevice || ''}
								onChange={(event) =>
								{
									if (event.target.value)
									{
										handleSpeakerSelected(event.target.value);
									}
								}}
								displayEmpty
								name={intl.formatMessage({
									id             : 'settings.audioOutput',
									defaultMessage : 'Audio output device'
								})}
								autoWidth
								classes={{
									root : classes.selectEmpty
								}}
								disabled={audioOutputDevices.length === 0 || me.audioOutputInProgress}
							>
								{ audioOutputDevices.map((audioOutput, index) =>
								{
									return (
										<MenuItem
											key={index}
											value={audioOutput.deviceId}
										>
											{audioOutput.label}
											{settings.selectedAudioOutputDevice === audioOutput.deviceId && ' (選択済)'}
										</MenuItem>
									);
								})}
							</Select>
						</Box>
					</FormControl>
				</form>
				}
				<Box className={classes.soundTest} ref={endOfcontentRef}>
					<Button
						className={classes.playSoundBtn}
						onClick={() => playSound()}
						startIcon={<VolumeUpIcon />}
					>
						スピーカーをテストする
					</Button>
				</Box>

			</React.Fragment>
		</MuiThemeProvider>
	);
};

DeviceSettings.propTypes =
{
	roomClient                   : PropTypes.object.isRequired,
	me                           : appPropTypes.Me.isRequired,
	room                         : PropTypes.object.isRequired,
	settings                     : PropTypes.object.isRequired,
	videoMuted                   : PropTypes.bool.isRequired,
	classes                      : PropTypes.object.isRequired,
	setSelectedWebcamDevice      : PropTypes.func.isRequired,
	setSelectedAudioDevice       : PropTypes.func.isRequired,
	setSelectedAudioOutputDevice : PropTypes.func.isRequired,
	isHost                       : PropTypes.bool,
	volumeCheck                  : PropTypes.bool,
	setChangePersonDialogOpen    : PropTypes.func.isRequired,
	peerVolumes                  : PropTypes.any,
	endOfcontentRef              : PropTypes.any,
	setPeerVolume                : PropTypes.func.isRequired
};

const mapStateToProps = (state) =>
{
	return {
		me          : state.me,
		room        : state.room,
		settings    : state.settings,
		videoMuted  : state.settings.videoMuted,
		peerVolumes : state.peerVolumes
	};
};

const mapDispatchToProps = (dispatch) =>
{
	return {
		setSelectedWebcamDevice : (device) =>
		{
			dispatch(settingsActions.setSelectedWebcamDevice(device));
		},
		setSelectedAudioDevice : (device) =>
		{
			dispatch(settingsActions.setSelectedAudioDevice(device));
		},
		setSelectedAudioOutputDevice : (device) =>
		{
			dispatch(settingsActions.setSelectedAudioOutputDevice(device));
		},
		setChangePersonDialogOpen : (flag) =>
		{
			dispatch(settingsActions.setChangePersonDialogOpen(flag));
		},
		setPeerVolume : (id, volume) =>
		{
			dispatch(peerVolumeActions.setPeerVolume(id, volume));
		}
	};
};

export default withRoomContext(connect(
	mapStateToProps,
	mapDispatchToProps,
	null,
	{
		areStatesEqual : (next, prev) =>
		{
			return (
				prev.me === next.me &&
				prev.room === next.room &&
				prev.settings === next.settings &&
				prev.peerVolumes === next.peerVolumes &&
				prev.settings.videoMuted === next.settings.videoMuted &&
				prev.settings.changePersonDialogOpen === next.settings.changePersonDialogOpen
			);
		}
	}
)(withStyles(styles)(DeviceSettings)));